import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import {
  AppShellRootComponent,
  injectProtectedEntry,
  injectShellRouteData,
} from '@sbe-spinoff-client/app-shell';
import { appRoutes } from './app.routes';
import { StaffAuthService } from './core/staff-auth.service';
import { StaffProfileMenuComponent } from './shared/staff-profile-menu/staff-profile-menu.component';

interface ShellRouteData extends Record<string, unknown> {
  hideLogout?: boolean;
}

@Component({
  imports: [RouterLink, MatButtonModule, AppShellRootComponent, StaffProfileMenuComponent],
  selector: 'app-root',
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- Angular 22 스키매틱이 기존 동작 보존을 위해 삽입(Nx 23 마이그레이션, 2026-08-25) — OnPush 전환은 별도 후속 작업
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <sbe-app-shell-root header="toolbar" title="SchoolBell-e 스태프" [showBootLoading]="showBootLoading()" [loggedIn]="isAuthenticated()">
      @if (showLogout()) {
        <app-staff-profile-menu shellToolbarActions (logoutRequested)="logout()" />
      } @else if (!isAuthenticated()) {
        <a shellToolbarActions mat-button routerLink="/login">로그인</a>
      }
    </sbe-app-shell-root>
  `,
})
export class App {
  private readonly auth = inject(StaffAuthService);
  private readonly router = inject(Router);

  /** 헤더는 모든 라우트에서 항상 보인다(2026-08-12 사용자 확인) — 라우트별로 감추지 않는다. */
  protected readonly shellRouteData = injectShellRouteData<ShellRouteData>({});

  /**
   * `invitation-accepted`/`oauth/authorize`/홈(`''`) 등 guard 보호 라우트로 콜드 진입/
   * 새로고침 시, `staffAuthGuard`가 `auth.me()`를 기다리는 동안 콘텐츠 영역에 로딩
   * 스피너를 표시한다 — 그 전엔 이 앱이 `AppShellRootComponent`를 쓰지 않아 빈 화면으로
   * 남았다(2026-08-13).
   */
  protected readonly isProtectedEntry = injectProtectedEntry(appRoutes);
  protected readonly showBootLoading = computed(
    () => this.isProtectedEntry && !this.auth.sessionChecked(),
  );

  /**
   * `staff` signal 은 표시용 캐시일 뿐이라(StaffAuthService 문서 참조) 여기서도 순수
   * UI 토글(로그아웃 버튼 노출)에만 쓴다 — 실제 인가 경계는 여전히 서버·가드가 담당한다.
   */
  protected readonly isAuthenticated = computed(() => !!this.auth.staff());

  /**
   * `/invitation`(초대 미수락 staff 블로킹 화면)은 `hideLogout: true`로 로그아웃만
   * 감춘다 — 헤더 자체는 다른 라우트와 동일하게 보이지만, 이 화면은 초대 미수락 staff가
   * 빠져나갈 수 없는 것으로 문서화돼 있어 로그아웃이라는 탈출 경로만은 막아야 한다.
   */
  protected readonly showLogout = computed(
    () => this.isAuthenticated() && !this.shellRouteData().hideLogout,
  );

  async logout(): Promise<void> {
    try {
      await this.auth.logout();
    } catch {
      // 세션이 이미 만료/폐기된 상태의 로그아웃 호출은 401로 실패할 수 있음 — 그래도 로그아웃
      // 흐름은 계속 진행한다(사용자를 현재 페이지에 가두지 않는다).
    } finally {
      await this.router.navigate(['/welcome']);
    }
  }
}
